import React, { useState } from 'react';
import { Button, CircleButton, EasyModal } from '@ui';
import useTranslation from '@app/hooks/use-translation';
import UserGroupForm from '@app/pages/users/blocks/user-group-form';
import {
  useDeleteUserGroupMutation,
  useGetUserGroupsBulkQuery,
  useGetUsersQuery,
  useLazyGetUserGroupInfoQuery,
} from '@app/store/api/users.api';
import RubberTable from '@app/components/rubber-table';
import { TableColumn } from '@app/components/rubber-table/table.types';
import { UserGroupType, UsersListRequestType } from '@app/interfaces/users';

const UsersGroupsPage = () => {
  const { t } = useTranslation('pages.usersPage');

  const [usersBulkRequestData, setUsersBulkRequestData] = useState<
    Omit<UsersListRequestType, 'filter'>
  >({ offset: 0, limit: 10 });
  // api
  const { data: usersList } = useGetUsersQuery(usersBulkRequestData);
  const { data: groupsList } = useGetUserGroupsBulkQuery();
  const [deleteUserGroup] = useDeleteUserGroupMutation();
  const [getGroupInfo] = useLazyGetUserGroupInfoQuery();
  // api
  const [userGroupModalState, setUserGroupModalState] = React.useState(false);
  const [groupIdForDelete, setGroupIdForDelete] = React.useState<string>();
  const [userGroupData, setUserGroupData] = React.useState<UserGroupType>();

  function closeModal() {
    setUserGroupModalState(false);
  }

  function handleDeleteGroup() {
    if (!groupIdForDelete) return;
    deleteUserGroup(groupIdForDelete);
    setGroupIdForDelete(undefined);
  }
  function handleOpenEditSettings(id: string) {
    getGroupInfo({ id })
      .unwrap()
      .then((groupInfo) => {
        setUserGroupData(groupInfo);
        setUserGroupModalState(true);
      });
  }

  const tableCols: TableColumn[] = [
    { title: t('users_groups_table.name'), index: 'name' },
    { title: t('users_groups_table.members'), index: 'members' },
    { title: t('users_groups_table.actions'), index: 'actions', maxWidth: 130 },
  ];

  const tableData = groupsList?.groups?.map((group) => ({
    ...group,
    actions: (
      <div className="flex  items-center gap-[5px] justify-center w-full">
        <CircleButton
          onClick={() => handleOpenEditSettings(group.id)}
          icon={'SettingIcon'}
          size={19}
        />
        <CircleButton
          onClick={() => setGroupIdForDelete(group.id)}
          icon={'TrashIcon'}
          disable={!!group.members}
          className="text-basic_red"
          size={19}
        />
      </div>
    ),
  }));

  return (
    <>
      <div className="flex w-full justify-end py-[30px]">
        <Button
          data-id="new-user"
          label={t('add_user_group_button')}
          // onClick={() => {
          //   changeOpenAddUserModal(true);
          //   setIsEditMode(false);
          //   setCurrentUserData(null);
          // }}
          onClick={() => {
            setUserGroupData(undefined);
            setUserGroupModalState(true);
          }}
          icon="CirclePlusIcon"
        />
      </div>
      <RubberTable dataSource={tableData || []} columns={tableCols} />
      <EasyModal
        label={t('users_groups_modal.title')}
        variant="largeW1127"
        withoutFooter
        show={userGroupModalState}
        onClose={closeModal}
      >
        {userGroupModalState && (
          <UserGroupForm
            setUsersBulkRequestData={setUsersBulkRequestData}
            usersBulkRequestData={usersBulkRequestData}
            userGroupData={userGroupData}
            onClose={closeModal}
            usersList={usersList}
            total={usersList?.total}
          />
        )}
      </EasyModal>
      <EasyModal
        withoutFooter
        onClose={() => setGroupIdForDelete(undefined)}
        label={t('warning')}
        show={!!groupIdForDelete}
        variant="removal"
        onRemove={handleDeleteGroup}
      >
        <div className="flex items-center justify-center">{t('delete_group_text')}</div>
      </EasyModal>
    </>
  );
};

export default UsersGroupsPage;
